import { graphql, useStaticQuery } from 'gatsby';
import React, { useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useCurrencies } from '@/bits';
import {
  Card,
  CardCloseButton,
  CardOptions,
  NakedForm,
  NumberField,
  SelectField,
  SelectOption,
  TextField,
  TextareaField,
  useModalContext,
} from '@/components';
import { SubmitButton } from '@/components/form/SubmitButton';
import { GameSearch } from '@/components/GameSearch';
import { useTranslate } from '@/contexts';
import {
  ChallengeRewardInputV2,
  ChallengeRewardTypeEnum,
  ClaimType,
  GameProvider,
  Maybe,
  Scalars,
} from '@/globalTypes';
import { rewardGameProviders } from '@/utils';
import { assert } from '@/utils/error';

const query = graphql`
  query SanityChallengeRewardCreateForm {
    sanityChallengeRewardCreateForm {
      title {
        ...SanityLocaleString
      }
      rewardType {
        ...SanityLocaleString
      }
      value {
        ...SanityLocaleString
      }
      percentMatch {
        ...SanityLocaleString
      }
      maxAmount {
        ...SanityLocaleString
      }
      maxFreeSpinsAmount {
        ...SanityLocaleString
      }
      currency {
        ...SanityLocaleString
      }
      numberOfSpins {
        ...SanityLocaleString
      }
      paylines {
        ...SanityLocaleString
      }
      game {
        ...SanityLocaleString
      }
      gameProviders {
        ...SanityLocaleString
      }
      animation {
        ...SanityLocaleString
      }
      maxPayout {
        ...SanityLocaleString
      }
      nameOfReward {
        ...SanityLocaleString
      }
      timeToActive {
        ...SanityLocaleString
      }
      validFor {
        ...SanityLocaleString
      }
      claimableInstant {
        ...SanityLocaleString
      }
      notes {
        ...SanityLocaleString
      }
      wageringFactor {
        ...SanityLocaleString
      }
    }
  }
`;

type ActiveAfter = '1.second' | '1.day' | '7.days' | '30.days';
type ValidFor = '1.day' | '7.days' | '30.days';
type RewardOption = SelectOption & { value: ChallengeRewardTypeEnum };
type ActiveAfterOption = SelectOption & { value: ActiveAfter };
type ValidForOption = SelectOption & { value: ValidFor };

enum PayoutType {
  Static = 'Static',
  Dynamic = 'Dynamic',
}

type FormValues = {
  rewardType: ChallengeRewardTypeEnum;
  gameProvider: GameProvider;
  activateAfter: string;
  expiresIn: string;
  claimType: ClaimType;
  note: string;
  spinsNumber: number | null;
  amount: number | null;
  multiplier: number | null;
  maxAmount: number | null;
  betLevel: number;
  currencyCode: Scalars['ISOCurrencyCode']['input'];
  gameDetails: {
    mobileId: string;
    desktopId: string;
  };
  wageringRequirementFactor: number | null;
  maxPayout: number | null;
  maxPayoutMultiplier: number | null;
  payoutType: PayoutType;
};

type Fields = keyof FormValues;

const fieldsByRewardType = {
  [ChallengeRewardTypeEnum.FreeSpinsType]: [
    'amount',
    'betLevel',
    'currencyCode',
    'gameDetails',
    'gameProvider',
    'spinsNumber',
  ],
  [ChallengeRewardTypeEnum.MoneyDropType]: ['amount', 'currencyCode'],
  [ChallengeRewardTypeEnum.BonusMoneyDropType]: [
    'amount',
    'currencyCode',
    'wageringRequirementFactor',
    'maxPayout',
  ],
  [ChallengeRewardTypeEnum.DynamicFreeSpinsType]: [
    'amount',
    'multiplier',
    'maxAmount',
    'betLevel',
    'currencyCode',
    'gameProvider',
    'gameDetails',
  ],
  [ChallengeRewardTypeEnum.DynamicMoneyDropType]: [
    'multiplier',
    'maxAmount',
    'currencyCode',
  ],
  [ChallengeRewardTypeEnum.DynamicBonusMoneyDropType]: [
    'multiplier',
    'maxAmount',
    'currencyCode',
    'wageringRequirementFactor',
    'payoutType',
    'maxPayout',
    'maxPayoutMultiplier',
  ],
};

const useOptions = (showDynamicRewards: boolean) => {
  return useMemo(() => {
    const fixedRewardTypesOptions: RewardOption[] = [
      {
        label: 'FreeSpins',
        value: ChallengeRewardTypeEnum.FreeSpinsType,
      },
      {
        label: 'MoneyDrop',
        value: ChallengeRewardTypeEnum.MoneyDropType,
      },
      {
        label: 'BonusMoneyDrop',
        value: ChallengeRewardTypeEnum.BonusMoneyDropType,
      },
    ];

    const dynamicRewardTypesOptions: RewardOption[] = [
      {
        label: 'DynamicFreeSpins',
        value: ChallengeRewardTypeEnum.DynamicFreeSpinsType,
      },
      {
        label: 'DynamicMoneyDrop',
        value: ChallengeRewardTypeEnum.DynamicMoneyDropType,
      },
      {
        label: 'DynamicBonusMoneyDrop',
        value: ChallengeRewardTypeEnum.DynamicBonusMoneyDropType,
      },
    ];

    const rewardTypesOptions = showDynamicRewards
      ? fixedRewardTypesOptions.concat(dynamicRewardTypesOptions)
      : fixedRewardTypesOptions;

    const claimOptions: SelectOption[] = [
      {
        label: 'Claimable',
        value: 'Manual',
      },
      {
        label: 'Instant',
        value: 'Instant',
      },
    ];

    const activeAfterOptions: ActiveAfterOption[] = [
      {
        label: 'Instant',
        value: '1.second',
      },
      {
        label: 'After 1 Hour',
        value: '1.day',
      },
      {
        label: 'After 1 Week',
        value: '7.days',
      },
      {
        label: 'After 1 Month',
        value: '30.days',
      },
    ];

    const validForOptions: ValidForOption[] = [
      {
        label: '1 Day',
        value: '1.day',
      },
      {
        label: '1 Week',
        value: '7.days',
      },
      {
        label: '1 Month',
        value: '30.days',
      },
    ];

    const payoutTypeOptions: SelectOption[] = [
      {
        label: 'Static',
        value: 'Static',
      },
      {
        label: 'Dynamic',
        value: 'Dynamic',
      },
    ];

    return {
      rewardTypesOptions,
      claimOptions,
      activeAfterOptions,
      validForOptions,
      payoutTypeOptions,
    };
  }, [showDynamicRewards]);
};

const getRewardTypeInput = (values: FormValues) => {
  // Multiplier for BE is in decimal value from 0 to 1. For user input, it's in percentage from 0 to 100
  const multiplier = values.multiplier ? Number(values.multiplier / 100) : 0;

  switch (values.rewardType) {
    case ChallengeRewardTypeEnum.MoneyDropType:
      return {
        moneyDrop: {
          amount: Number(values.amount),
          currency: values.currencyCode,
        },
        challengeRewardType: values.rewardType,
      };
    case ChallengeRewardTypeEnum.BonusMoneyDropType:
      return {
        bonusMoneyDrop: {
          amount: Number(values.amount),
          currency: values.currencyCode,
          wageringRequirementFactor: Number(values.wageringRequirementFactor),
          maxPayout: Number(values.maxPayout),
        },
        challengeRewardType: values.rewardType,
      };
    case ChallengeRewardTypeEnum.DynamicFreeSpinsType:
      return {
        dynamicFreeSpins: {
          multiplier,
          maxFreeSpinsAmount: Number(values.maxAmount),
          freeSpinValue: Number(values.amount),
          payLines: Number(values.betLevel),
          provider: values.gameProvider,
          currency: values.currencyCode,
          desktopId: values.gameDetails.desktopId,
          mobileId: values.gameDetails.mobileId,
        },
        challengeRewardType: values.rewardType,
      };
    case ChallengeRewardTypeEnum.DynamicMoneyDropType:
      return {
        dynamicMoneyDrop: {
          multiplier,
          maxAmount: Number(values.maxAmount),
          currency: values.currencyCode,
        },
        challengeRewardType: values.rewardType,
      };
    case ChallengeRewardTypeEnum.DynamicBonusMoneyDropType:
      return {
        dynamicBonusMoneyDrop: {
          multiplier,
          maxAmount: Number(values.maxAmount),
          currency: values.currencyCode,
          wageringRequirementFactor: Number(values.wageringRequirementFactor),
          maxPayout: Number(values.maxPayout),
          maxPayoutMultiplier: Number(values.maxPayoutMultiplier),
        },
        challengeRewardType: values.rewardType,
      };
    default:
      return {
        freeSpins: {
          freeSpinValue: Number(values.amount),
          amountFreeSpins: Number(values.spinsNumber),
          payLines: Number(values.betLevel),
          provider: values.gameProvider,
          currency: values.currencyCode,
          desktopId: values.gameDetails.desktopId,
          mobileId: values.gameDetails.mobileId,
        },
        challengeRewardType: values.rewardType,
      };
  }
};

type Props = {
  onCompleted: (reward: ChallengeRewardInputV2) => void;
  showDynamicRewards: boolean;
  challengeCurrency: Maybe<Scalars['ISOCurrencyCode']['input']>;
};

const ChallengeRewardCreateForm = ({
  onCompleted,
  showDynamicRewards = false,
  challengeCurrency,
}: Props) => {
  const { t } = useTranslate();
  const staticData =
    useStaticQuery<Queries.SanityChallengeRewardCreateFormQuery>(query);
  const { close } = useModalContext();

  const form = staticData.sanityChallengeRewardCreateForm;
  assert(form, 'missing form data');

  const {
    rewardTypesOptions,
    claimOptions,
    activeAfterOptions,
    validForOptions,
    payoutTypeOptions,
  } = useOptions(showDynamicRewards);

  const { currencyOptions } = useCurrencies();

  const defaultValues: FormValues = useMemo(
    () => ({
      rewardType: ChallengeRewardTypeEnum.MoneyDropType,
      gameProvider: GameProvider.Relax,
      activateAfter: '1.second',
      expiresIn: '7.days',
      claimType: ClaimType.Manual,
      note: '',
      amount: null,
      spinsNumber: null,
      betLevel: 1,
      currencyCode: challengeCurrency || 'EUR',
      gameDetails: {
        desktopId: '',
        mobileId: '',
      },
      wageringRequirementFactor: null,
      payoutType: PayoutType.Dynamic,
      maxPayout: null,
      maxPayoutMultiplier: null,
      multiplier: null,
      maxAmount: null,
    }),
    [challengeCurrency],
  );

  const methods = useForm<FormValues>({
    defaultValues,
  });

  const { watch, reset } = methods;

  const rewardType = watch('rewardType');
  const selectedGameProvider = watch('gameProvider');
  const isDynamicType = watch('payoutType') === PayoutType.Dynamic;

  const isBonusMoneyDropType =
    watch('rewardType') === ChallengeRewardTypeEnum.BonusMoneyDropType;

  useEffect(() => {
    reset({ ...defaultValues, rewardType });
  }, [rewardType, reset, defaultValues]);

  const showField = (fieldName: Fields) => {
    const fields =
      fieldsByRewardType[rewardType as keyof typeof fieldsByRewardType];
    return fields ? fields.includes(fieldName) : false;
  };

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    const challengeRewardTypeInput = getRewardTypeInput(values);

    onCompleted({
      challengeRewardTypeInput,
      activateAfter: values.activateAfter,
      expiresIn: values.expiresIn,
      claimType: values.claimType,
    });

    if (close) {
      close();
    }
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="flex p-6">
        <NakedForm className="w-full" onSubmit={onSubmit} methods={methods}>
          <div className="flex sm:flex-row flex-col space-x-6 pb-6">
            <div className="flex-1 space-y-4">
              <SelectField
                name="rewardType"
                title="Reward Type"
                required
                options={rewardTypesOptions}
              />
              <div className="grid sm:grid-cols-2 gap-2">
                <>
                  {showField('multiplier') && (
                    <TextField name="multiplier" required title="% match" />
                  )}
                  {showField('maxAmount') && (
                    <TextField name="maxAmount" required title="Max. Amount" />
                  )}
                  {showField('amount') && (
                    <TextField name="amount" required title="Value" />
                  )}
                  {showField('spinsNumber') && (
                    <TextField
                      name="spinsNumber"
                      required
                      title="Nr. of Spins"
                    />
                  )}
                  <SelectField
                    name="currencyCode"
                    required
                    title="Currency"
                    options={currencyOptions}
                    disabled={!!challengeCurrency}
                  />
                  {showField('betLevel') && (
                    <NumberField
                      name="betLevel"
                      required
                      title="Paylines"
                      step={1}
                    />
                  )}
                  {showField('wageringRequirementFactor') && (
                    <NumberField
                      name="wageringRequirementFactor"
                      required
                      title="Wagering Factor"
                    />
                  )}
                  {showField('payoutType') && (
                    <SelectField
                      name="payoutType"
                      title="Payout Type"
                      options={payoutTypeOptions}
                      required
                    />
                  )}
                  {isBonusMoneyDropType && showField('maxPayout') && (
                    <TextField name="maxPayout" required title="Max. Payout" />
                  )}
                  {isDynamicType
                    ? showField('maxPayoutMultiplier') && (
                        <NumberField
                          name="maxPayoutMultiplier"
                          required
                          title="Max. Payout Factor"
                        />
                      )
                    : showField('maxPayout') && (
                        <TextField
                          name="maxPayout"
                          required
                          title="Max. Payout"
                        />
                      )}
                </>
                <div className="grid col-span-2 gap-2">
                  {showField('gameProvider') && (
                    <SelectField
                      name="gameProvider"
                      title="Game Provider"
                      options={rewardGameProviders}
                      required
                    />
                  )}
                  {showField('gameDetails') && (
                    <GameSearch
                      name="gameDetails"
                      id="challengeRewardCreateForm__gameDetails"
                      title="Game"
                      providerName={selectedGameProvider}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-4">
              <SelectField
                name="activateAfter"
                title="Activate After"
                options={activeAfterOptions}
                required
              />
              <SelectField
                name="expiresIn"
                title="Expires In (days)"
                options={validForOptions}
                required
              />
              <SelectField
                name="claimType"
                title="Claimable/Instant"
                options={claimOptions}
                required
              />
            </div>
            <div className="flex-1 space-y-4">
              <TextareaField name="note" title="Notes" />
            </div>
          </div>
          <div className="flex justify-end">
            <SubmitButton value="Add Reward" />
          </div>
        </NakedForm>
      </div>
    </Card>
  );
};

export default ChallengeRewardCreateForm;
